import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Plus, X } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import {
  apiService,
  extractApiError,
  extractApiErrorField,
} from "../services/api";
import type {
  OnboardingField,
  OnboardingFormValues,
  OnboardingRequest,
  OnboardingRequestType,
} from "../types";
import {
  initialOnboardingValues,
  onboardingFields,
  toOnboardingPayload,
  validateOnboardingValues,
} from "../utils/onboardingRequests";
import FieldLabel from "./FieldLabel";

const AVAILABILITY_DEBOUNCE_MS = 400;

interface OnboardingRequestFormProps {
  type: OnboardingRequestType;
  request?: OnboardingRequest;
  onSuccess: (request: OnboardingRequest) => void;
  onCancel?: () => void;
}

export default function OnboardingRequestForm({
  type,
  request,
  onSuccess,
  onCancel,
}: OnboardingRequestFormProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isEdit = Boolean(request);
  const [values, setValues] = useState<OnboardingFormValues>(() =>
    initialOnboardingValues(type, user, request?.fields),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const latestCheck = useRef("");

  const namespaceField = onboardingFields(type).find(
    (f) => f.availabilityCheck === "namespace",
  );
  const namespaceValue =
    namespaceField && typeof values[namespaceField.key] === "string"
      ? (values[namespaceField.key] as string).trim()
      : "";
  const originalNamespace =
    namespaceField && typeof request?.fields[namespaceField.key] === "string"
      ? (request?.fields[namespaceField.key] as string)
      : "";

  useEffect(() => {
    if (!namespaceField || namespaceValue === "") return;
    if (namespaceValue === originalNamespace) return;
    if (
      namespaceField.pattern &&
      !new RegExp(namespaceField.pattern).test(namespaceValue)
    ) {
      return;
    }
    const key = namespaceField.key;
    const label = namespaceField.label;
    latestCheck.current = namespaceValue;
    const handle = setTimeout(() => {
      apiService
        .checkNamespaceAvailability(namespaceValue)
        .then((res) => {
          if (latestCheck.current !== namespaceValue) return;
          const available = Boolean(
            (res.data?.data as { available?: boolean } | undefined)?.available,
          );
          if (available) return;
          setErrors((prev) => ({
            ...prev,
            [key]: `${label} ${namespaceValue} has already been requested`,
          }));
        })
        .catch(() => undefined);
    }, AVAILABILITY_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [namespaceField, namespaceValue, originalNamespace]);

  const businessArea =
    typeof values.businessArea === "string" ? values.businessArea : "";

  const businessAreasQuery = useQuery(
    "businessAreas",
    () => apiService.getBusinessAreas(),
    {
      select: (res) => (res.data?.success ? (res.data.data as string[]) : []),
    },
  );
  const divisionsQuery = useQuery(
    ["divisions", businessArea],
    () => apiService.getDivisions(businessArea),
    {
      enabled: businessArea !== "",
      select: (res) => (res.data?.success ? (res.data.data as string[]) : []),
    },
  );

  const mutation = useMutation(
    () =>
      request
        ? apiService.updateOnboardingRequest(
            request.id,
            toOnboardingPayload(type, values),
          )
        : apiService.submitOnboardingRequest(
            type.slug,
            toOnboardingPayload(type, values),
          ),
    {
      onSuccess: (res) => {
        if (res.data?.success && res.data.data) {
          queryClient.invalidateQueries("onboarding-requests");
          onSuccess(res.data.data as OnboardingRequest);
        }
      },
      onError: (error) => {
        const field = extractApiErrorField(error);
        if (field) {
          setErrors((prev) => ({ ...prev, [field]: extractApiError(error) }));
        }
      },
    },
  );

  const setValue = (key: string, value: string | string[]) => {
    setValues((prev) => {
      const next = { ...prev, [key]: value };
      if (key === "businessArea") next.division = "";
      return next;
    });
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validation = validateOnboardingValues(type, values);
    setErrors(validation);
    if (Object.keys(validation).length > 0) return;
    mutation.mutate();
  };

  const submitError =
    mutation.isError && !extractApiErrorField(mutation.error)
      ? extractApiError(mutation.error)
      : null;
  const disabled = mutation.isLoading;

  const renderField = (field: OnboardingField) => {
    const id = `onboarding-${type.slug}-${field.key}`;
    const raw = values[field.key];
    const text = typeof raw === "string" ? raw : "";
    const list = Array.isArray(raw) ? raw : [];
    const error = errors[field.key];
    const inputClass = `input ${error ? "border-red-500" : ""}`;

    let control: React.ReactNode;
    switch (field.kind) {
      case "textarea":
        control = (
          <textarea
            id={id}
            value={text}
            onChange={(e) => setValue(field.key, e.target.value)}
            className={`${inputClass} min-h-[5rem]`}
            placeholder={field.placeholder}
            maxLength={field.maxLength}
            disabled={disabled}
          />
        );
        break;
      case "select":
        control = (
          <select
            id={id}
            value={text}
            onChange={(e) => setValue(field.key, e.target.value)}
            className={inputClass}
            disabled={disabled}
          >
            <option value="" disabled>
              Select an option
            </option>
            {field.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
        break;
      case "business-area":
        control = (
          <select
            id={id}
            value={text}
            onChange={(e) => setValue(field.key, e.target.value)}
            className={inputClass}
            disabled={disabled || businessAreasQuery.isLoading}
          >
            <option value="">Select a business area</option>
            {(businessAreasQuery.data ?? []).map((area) => (
              <option key={area} value={area}>
                {area}
              </option>
            ))}
          </select>
        );
        break;
      case "division":
        control = (
          <select
            id={id}
            value={text}
            onChange={(e) => setValue(field.key, e.target.value)}
            className={inputClass}
            disabled={
              disabled || businessArea === "" || divisionsQuery.isLoading
            }
          >
            <option value="">
              {businessArea === ""
                ? "Select a business area first"
                : "Select a division"}
            </option>
            {(divisionsQuery.data ?? []).map((division) => (
              <option key={division} value={division}>
                {division}
              </option>
            ))}
          </select>
        );
        break;
      case "list":
        control = (
          <div className="space-y-2">
            {list.map((entry, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="text"
                  aria-label={`${field.label} ${index + 1}`}
                  value={entry}
                  onChange={(e) => {
                    const next = [...list];
                    next[index] = e.target.value;
                    setValue(field.key, next);
                  }}
                  className={inputClass}
                  placeholder={field.placeholder}
                  disabled={disabled}
                />
                <button
                  type="button"
                  className="btn btn-secondary px-2"
                  aria-label={`Remove ${field.label} ${index + 1}`}
                  onClick={() =>
                    setValue(
                      field.key,
                      list.filter((_, i) => i !== index),
                    )
                  }
                  disabled={disabled}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn btn-secondary inline-flex items-center gap-1"
              onClick={() => setValue(field.key, [...list, ""])}
              disabled={disabled}
            >
              <Plus className="h-4 w-4" />
              Add entry
            </button>
          </div>
        );
        break;
      default:
        control = (
          <input
            id={id}
            type={
              field.kind === "email"
                ? "email"
                : field.kind === "number"
                  ? "number"
                  : "text"
            }
            value={text}
            onChange={(e) => setValue(field.key, e.target.value)}
            className={inputClass}
            placeholder={field.placeholder}
            maxLength={field.maxLength}
            min={field.minimum}
            disabled={disabled}
          />
        );
    }

    return (
      <div key={field.key}>
        <FieldLabel
          htmlFor={field.kind === "list" ? undefined : id}
          description={field.description}
        >
          {field.label}
          {field.required && <span className="ml-1 text-red-500">*</span>}
        </FieldLabel>
        {control}
        {error ? (
          <p
            role="alert"
            className="mt-1 text-xs text-red-600 dark:text-red-400"
          >
            {error}
          </p>
        ) : (
          field.help && (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {field.help}
            </p>
          )
        )}
      </div>
    );
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {type.groups.map((group) => (
        <fieldset key={group.title} className="card space-y-4">
          <legend className="sr-only">{group.title}</legend>
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {group.title}
            </h3>
            {group.description && (
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                {group.description}
              </p>
            )}
          </div>
          {group.fields.map(renderField)}
        </fieldset>
      ))}

      {submitError && (
        <div role="alert" className="text-sm text-red-600 dark:text-red-400">
          {submitError}
        </div>
      )}

      <div className="flex items-center justify-end gap-3">
        {onCancel && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={disabled}
          >
            Cancel
          </button>
        )}
        <button type="submit" className="btn btn-primary" disabled={disabled}>
          {disabled
            ? isEdit
              ? "Saving..."
              : "Submitting..."
            : isEdit
              ? "Save changes"
              : "Submit request"}
        </button>
      </div>
    </form>
  );
}
